import React, { useEffect, useState, useCallback } from 'react';
import {
  ShieldCheck, CheckCircle2, XCircle, Clock, RefreshCw, Smartphone,
  Loader2, AlertTriangle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { subscriptionsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_BADGES = {
  pending_review: { label: 'En attente', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  approved: { label: 'Validee', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  rejected: { label: 'Rejetee', cls: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
};

const fmtMoney = (a, c = 'xof') =>
  `${Math.round(Number(a) || 0).toLocaleString('fr-FR')} ${(c || 'XOF').toUpperCase() === 'XOF' ? 'FCFA' : c.toUpperCase()}`;
const fmtDateTime = (s) => {
  if (!s) return '-';
  try { return format(new Date(s), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch { return s; }
};

export default function AdminMobilePayments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  // Reject modal
  const [showReject, setShowReject] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const isSuperAdmin = user && user.role === 'super_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await subscriptionsAPI.listMobilePayments(filter !== 'all' ? filter : null);
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto text-red-500 mb-3" />
        <h2 className="text-xl font-bold text-red-700">Acces refuse</h2>
        <p className="text-gray-600 mt-2">Cette page est reservee aux super-administrateurs.</p>
      </div>
    );
  }

  const handleApprove = async (id) => {
    if (!window.confirm('Valider ce paiement et activer l\'abonnement de l\'agence ?')) return;
    setSubmitting(true);
    try {
      const r = await subscriptionsAPI.approveMobilePayment(id);
      toast.success(r.data?.message || 'Paiement valide');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  const openReject = (id) => {
    setRejectId(id);
    setRejectReason('');
    setShowReject(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Motif obligatoire'); return; }
    setSubmitting(true);
    try {
      await subscriptionsAPI.rejectMobilePayment(rejectId, rejectReason.trim());
      toast.success('Declaration rejetee');
      setShowReject(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  const stats = {
    total: items.length,
    pending: items.filter((i) => i.status === 'pending_review').length,
    approved: items.filter((i) => i.status === 'approved').length,
    rejected: items.filter((i) => i.status === 'rejected').length,
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="admin-mobile-payments-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1B3A4B] flex items-center gap-2">
            <ShieldCheck className="h-8 w-8 text-[#D4A745]" />
            Validation paiements Mobile Money
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Validez les declarations de paiement Mobile Money envoyees par les agences pour activer / renouveler leur abonnement.
          </p>
        </div>
        <Button variant="outline" onClick={load}
                data-testid="refresh-btn"
                className="border-[#1B3A4B] text-[#1B3A4B]">
          <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-gray-500">Total</div>
          <div className="text-2xl font-bold text-[#1B3A4B]">{stats.total}</div>
        </CardContent></Card>
        <Card className="border-amber-200"><CardContent className="p-4">
          <div className="text-xs uppercase text-amber-700">En attente</div>
          <div className="text-2xl font-bold text-amber-700">{stats.pending}</div>
        </CardContent></Card>
        <Card className="border-emerald-200"><CardContent className="p-4">
          <div className="text-xs uppercase text-emerald-700">Validees</div>
          <div className="text-2xl font-bold text-emerald-700">{stats.approved}</div>
        </CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4">
          <div className="text-xs uppercase text-red-700">Rejetees</div>
          <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
        </CardContent></Card>
      </div>

      {/* Filter */}
      <Card><CardContent className="p-3 flex items-center gap-3">
        <Label className="text-xs">Filtrer par statut</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-64" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="pending_review">En attente</SelectItem>
            <SelectItem value="approved">Validees</SelectItem>
            <SelectItem value="rejected">Rejetees</SelectItem>
          </SelectContent>
        </Select>
      </CardContent></Card>

      {/* List */}
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1B3A4B] text-white">
              <tr>
                <th className="text-left p-3">Date declaration</th>
                <th className="text-left p-3">Agence</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-left p-3">Mode</th>
                <th className="text-left p-3">Expediteur</th>
                <th className="text-left p-3">Reference</th>
                <th className="text-right p-3">Montant</th>
                <th className="text-center p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="p-8 text-center text-gray-500">
                  <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                </td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-gray-500">
                  Aucune declaration de paiement.
                </td></tr>
              )}
              {items.map((it) => {
                const Sb = STATUS_BADGES[it.status] || STATUS_BADGES.pending_review;
                const SIcon = Sb.icon;
                return (
                  <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`payment-row-${it.id}`}>
                    <td className="p-3 text-xs">{fmtDateTime(it.created_at)}</td>
                    <td className="p-3 font-semibold text-[#1B3A4B]">{it.agency_name || it.agency_id?.slice(0, 8)}</td>
                    <td className="p-3 capitalize">{it.plan}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <Smartphone className="h-3 w-3" />
                        {it.method === 'orange_money' ? 'Orange Money'
                          : it.method === 'jemenipay' ? 'Jɛmɛnipay'
                          : it.method}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-xs">{it.sender_msisdn}</td>
                    <td className="p-3 font-mono text-xs">{it.reference}</td>
                    <td className="p-3 text-right font-semibold text-[#1B3A4B]">
                      {fmtMoney(it.amount, it.currency)}
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={`${Sb.cls} border`}>
                        <SIcon className="h-3 w-3 mr-1 inline" />{Sb.label}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      {it.status === 'pending_review' && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm"
                                  onClick={() => handleApprove(it.id)}
                                  disabled={submitting}
                                  data-testid={`approve-${it.id}`}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Valider
                          </Button>
                          <Button size="sm" variant="outline"
                                  onClick={() => openReject(it.id)}
                                  data-testid={`reject-${it.id}`}
                                  className="border-red-500 text-red-600 hover:bg-red-50">
                            <XCircle className="h-4 w-4 mr-1" /> Rejeter
                          </Button>
                        </div>
                      )}
                      {it.status === 'approved' && (
                        <div className="text-xs text-emerald-700">
                          Par {it.approved_by_name}<br />
                          le {fmtDateTime(it.approved_at)}
                        </div>
                      )}
                      {it.status === 'rejected' && (
                        <div className="text-xs text-red-700" title={it.rejection_reason}>
                          Par {it.rejected_by_name}<br />
                          le {fmtDateTime(it.rejected_at)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Note details for rejected */}
      {items.some((i) => i.status === 'rejected' && i.rejection_reason) && (
        <Card><CardContent className="p-4">
          <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" /> Motifs des rejets recents
          </h3>
          <div className="space-y-2 text-xs">
            {items.filter((i) => i.status === 'rejected' && i.rejection_reason).slice(0, 5).map((i) => (
              <div key={i.id} className="border-l-2 border-red-300 pl-2">
                <span className="font-semibold">{i.agency_name}</span> - {i.reference} : {i.rejection_reason}
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* Reject Modal */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="max-w-md" data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center">
              <XCircle className="h-5 w-5 mr-2" /> Rejeter la declaration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motif du rejet *</Label>
            <Textarea rows={4} value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Ex: Reference de transaction non retrouvee, montant incorrect, doublon..."
                      data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>Annuler</Button>
            <Button onClick={handleReject} disabled={submitting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-reject-btn">
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
